import { FormState } from 'final-form';
import { action, computed, IObservableArray, observable } from 'mobx';
import { isNil } from 'ramda';
import { SeatConfigurationData } from '~admin/model/seat-configuration-data';
import { SeatConfigurationSectionData } from '~client/model/admin/seat-configuration-section-data';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { MarketplaceFormData } from '~admin/view/components/details/marketplace-details/marketplace-details-fields.component';
import { AccessibilityType } from '~client/model/accessibility-type';
import { isNilOrEmpty } from '~common/helper/global';

export enum ToolMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export class CreateEditEventFlowStore {
  mode: ToolMode;
  eventId: number;
  @observable formState: FormState<EventFormData>;
  @observable seatConfigurations: IObservableArray<SeatConfigurationData>;
  @observable availableSections: IObservableArray<SeatConfigurationSectionData>;

  constructor(mode: ToolMode) {
    this.mode = mode;
    this.availableSections = observable.array([]);
  }

  /**
   * Returns TRUE if CreateEditEventFlowStore is in Create Mode
   */
  @computed
  get isCreate(): boolean {
    return this.mode === ToolMode.CREATE;
  }

  /**
   * Returns TRUE if CreateEditEventFlowStore is in Edit Mode
   */
  @computed
  get isEdit(): boolean {
    return this.mode === ToolMode.EDIT;
  }

  /**
   * Returns all marketplaces from the form state values
   */
  @computed
  get marketplaces(): MarketplaceFormData[] {
    return this.formState.values.marketplaces;
  }

  /**
   * Returns eventDateTimeStart and eventDateTimeEnd
   */
  @computed
  get eventDates(): { eventDateTimeStart: string; eventDateTimeEnd: string } {
    return {
      eventDateTimeStart: this.formState.values.eventDateTimeStart,
      eventDateTimeEnd: this.formState.values.eventDateTimeEnd,
    };
  }

  /**
   * Returns eventLocationId
   */
  @computed
  get eventLocationId(): number | null {
    return this.formState.values.eventLocationId;
  }

  /**
   * Returns eventAccessibilityType
   */
  @computed
  get eventAccessibilityType(): AccessibilityType | null {
    return this.formState.values.eventAccessibilityType;
  }

  /**
   * Returns seatConfigurationId
   */
  @computed
  get seatConfigurationId(): number | null {
    return this.formState.values.seatConfigurationId;
  }

  /**
   * Returns the unselected sections
   */
  @computed
  get sectionsNotSelected(): SeatConfigurationSectionData[] {
    return this.availableSections?.filter(
      (section) => !this.selectedSectionCodes.includes(section.code),
    );
  }

  /**
   * Returns the selected sections
   */
  @computed
  get selectedSectionCodes(): number[] {
    return this.formState.values.marketplaces
      .flatMap((marketplace) => marketplace.offerGroups)
      .flatMap((offerGroup) => offerGroup.sections)
      .filter((section) => !isNilOrEmpty(section));
  }

  @action
  setEventId(eventId: number): void {
    this.eventId = eventId;
  }

  @action
  updateState(state: FormState<EventFormData>): void {
    this.formState = state;
    if (!isNil(state.values.seatConfigurationId)) {
      this.updateAvailableSectionList(this.seatConfigurationId);
    }
  }

  @action
  updateSeatConfigurations(seatConfigurations: SeatConfigurationData[]): void {
    this.seatConfigurations = observable.array(seatConfigurations);
  }

  @action
  updateAvailableSectionList(seatConfigurationId: number): void {
    const seatConfiguration = this.seatConfigurations?.find(
      (seatConfiguration: SeatConfigurationData) => seatConfiguration.id === seatConfigurationId,
    );
    this.availableSections = observable.array(seatConfiguration?.sectionList);
  }
}



// WEBPACK FOOTER //
// ./store/create-edit-event-flow-store.ts